import type { DbEnv } from "common/constants/db";
import {
  cognitoConfigForEnv,
  type EnvironmentKey,
  resolveRuntimeEnvFromHostname,
  type Server,
} from "common/constants/env";
import { POSTHOG_API_KEY } from "common/constants/marketing";

/* eslint-disable @typescript-eslint/naming-convention */
export interface EnvDetails {
  DEVELOP: boolean;
  STAGE: boolean;
  PROD: boolean;
  NOT_PROD: boolean;
  NOT_STAGE_OR_PROD: boolean;
  STAGE_OR_PROD: boolean;
}

interface FlagsConfig {
  DEBUG?: string;
  TEST_E2E_MODE?: string;
  GIT_TAG?: string;
  GIT_COMMIT_HASH?: string;
  GIT_BRANCH?: string;
  IS_DEV_MODE?: string;
  DB_ENV?: DbEnv;
  COGNITO_ENV?: Server;
  COGNITO_CLIENT_ID?: string;
  COGNITO_USER_POOL_ID?: string;
  COGNITO_DOMAIN?: string;
  RENDER_DEBUG?: string;
  ENABLE_CLIENT_RELOAD_ON_DEPLOYMENT_CHANGE?: string;
}

/**
 * Exposes build- and environment-related constants to the client. Build-time values (git
 * metadata, debug flags) come from Vite `define` replacements; environment identity
 * (Cognito, DB env) is resolved at runtime from the browser hostname so a single built
 * artifact serves every environment. Not meaningful server-side, where `window` is absent.
 */
export default class Flags {
  // Initializing these so that they can overwritten for tests
  public static config: FlagsConfig = {
    DEBUG: process.env.PUBLIC_DEBUG,
    TEST_E2E_MODE: process.env.PUBLIC_TEST_E2E_MODE,
    GIT_TAG: process.env.PUBLIC_GIT_TAG,
    GIT_COMMIT_HASH: process.env.PUBLIC_GIT_COMMIT_HASH,
    GIT_BRANCH: process.env.PUBLIC_GIT_BRANCH,
    IS_DEV_MODE: process.env.PUBLIC_IS_DEV_MODE,
    RENDER_DEBUG: process.env.PUBLIC_RENDER_DEBUG,
    ENABLE_CLIENT_RELOAD_ON_DEPLOYMENT_CHANGE: process.env.PUBLIC_ENABLE_CLIENT_RELOAD_ON_DEPLOYMENT_CHANGE,
  };

  public static absoluteUrl(relativeUrl: string): string {
    return window.location.origin + relativeUrl;
  }

  public static get TEST_E2E_MODE(): boolean {
    if (typeof Flags.config.TEST_E2E_MODE === "string") {
      return Flags.config.TEST_E2E_MODE === "true";
    }
    return !!Flags.config.TEST_E2E_MODE;
  }

  public static get GIT_TAG(): string {
    return Flags.config.GIT_TAG ?? "";
  }

  public static get GIT_COMMIT_HASH(): string {
    return Flags.config.GIT_COMMIT_HASH ?? "";
  }

  public static get GIT_BRANCH(): string {
    return Flags.config.GIT_BRANCH ?? "";
  }

  public static get DEBUG(): boolean {
    if (typeof Flags.config.DEBUG === "string") {
      return Flags.config.DEBUG === "true";
    }

    return !!Flags.config.DEBUG;
  }

  public static get IS_DEV_MODE(): boolean {
    if (typeof Flags.config.IS_DEV_MODE === "string") {
      return Flags.config.IS_DEV_MODE === "true";
    }

    return !!Flags.config.IS_DEV_MODE;
  }

  /**
   * Deploy environment resolved from the browser hostname, so one built artifact serves every
   * environment. Returns undefined off the browser (server / tests without a URL), where callers
   * fall back to the explicit config override instead.
   */
  private static envFromHostname(): EnvironmentKey | undefined {
    if (typeof window === "undefined" || !window.location) {
      return undefined;
    }
    return resolveRuntimeEnvFromHostname(window.location.hostname);
  }

  public static get DB_ENV(): DbEnv {
    return (Flags.config.DB_ENV ?? Flags.envFromHostname()) as DbEnv;
  }

  public static get COGNITO_ENV(): Server {
    return (Flags.config.COGNITO_ENV ?? Flags.envFromHostname()) as Server;
  }

  public static get COGNITO_CLIENT_ID(): string {
    if (Flags.config.COGNITO_CLIENT_ID) {
      return Flags.config.COGNITO_CLIENT_ID;
    }
    const env = Flags.config.COGNITO_ENV ?? Flags.envFromHostname();
    return env ? cognitoConfigForEnv(env).clientId : "";
  }

  public static get COGNITO_USER_POOL_ID(): string {
    if (Flags.config.COGNITO_USER_POOL_ID) {
      return Flags.config.COGNITO_USER_POOL_ID;
    }
    const env = Flags.config.COGNITO_ENV ?? Flags.envFromHostname();
    return env ? cognitoConfigForEnv(env).userPoolId : "";
  }

  public static get COGNITO_DOMAIN(): string {
    if (Flags.config.COGNITO_DOMAIN) {
      return Flags.config.COGNITO_DOMAIN;
    }
    const env = Flags.config.COGNITO_ENV ?? Flags.envFromHostname();
    return env ? cognitoConfigForEnv(env).userPoolDomain : "";
  }

  public static get RENDER_DEBUG(): boolean {
    if (typeof Flags.config.RENDER_DEBUG === "string") {
      return Flags.config.RENDER_DEBUG === "true";
    }
    return !!Flags.config.RENDER_DEBUG;
  }

  public static get ENABLE_CLIENT_RELOAD_ON_DEPLOYMENT_CHANGE(): boolean {
    if (typeof Flags.config.ENABLE_CLIENT_RELOAD_ON_DEPLOYMENT_CHANGE === "string") {
      return Flags.config.ENABLE_CLIENT_RELOAD_ON_DEPLOYMENT_CHANGE === "true";
    }
    return !!Flags.config.ENABLE_CLIENT_RELOAD_ON_DEPLOYMENT_CHANGE;
  }

  public static get APP_ENV(): EnvDetails {
    const env = Flags.config.COGNITO_ENV ?? Flags.envFromHostname();
    const isStage = env === "stage";
    const isProd = env === "prod";
    return {
      DEVELOP: env === "develop",
      STAGE: isStage,
      PROD: isProd,
      NOT_PROD: !isProd,
      NOT_STAGE_OR_PROD: !isProd && !isStage,
      STAGE_OR_PROD: isStage || isProd,
    };
  }

  public static get POSTHOG_API_KEY(): string {
    // Only capture events on prod or debug mode
    return Flags.DB_ENV === "prod" || Flags.DEBUG ? POSTHOG_API_KEY : "";
  }

  public static print(): void {
    console.log(
      JSON.stringify(
        {
          DEBUG: Flags.DEBUG,
          GIT_BRANCH: Flags.GIT_BRANCH,
          GIT_COMMIT_HASH: Flags.GIT_COMMIT_HASH,
          GIT_TAG: Flags.GIT_TAG,
          IS_DEV_MODE: Flags.IS_DEV_MODE,
          TEST_E2E_MODE: Flags.TEST_E2E_MODE,
          DB_ENV: Flags.DB_ENV,
          COGNITO_ENV: Flags.COGNITO_ENV,
          RENDER_DEBUG: Flags.RENDER_DEBUG,
          ENABLE_CLIENT_RELOAD_ON_DEPLOYMENT_CHANGE: Flags.ENABLE_CLIENT_RELOAD_ON_DEPLOYMENT_CHANGE,
        },
        null,
        2,
      ),
    );
  }
}
